import * as React from "react";
import { getIntrinsicElementProps, slot } from "@fluentui/react-components";
import type {
  AppPreviewGalleryInternalState,
  AppPreviewGalleryProps,
  AppPreviewGalleryState,
  NeededPropsFromAppGalleryButton,
} from "./AppPreviewGallery.types";
import { UILocation, dataUniqueId, idWithSuffix } from "@1js/acui-common";
import { ghostFlyoutIdSuffix } from "../AppPreviewGalleryConsts";
import { AppPreviewGalleryFlyoutAnchor } from "./AppPreviewGalleryFlyoutAnchor";
import type { AppPreviewGalleryFlyoutAnchorProps } from "./AppPreviewGalleryFlyoutAnchor.types";
import { useResponsiveRenderingIsEnabled } from "../hooks/useResponsiveRenderingIsEnabled";
import { useOnFocus } from "../hooks/useOnFocus";
import { useOnBlur } from "../hooks/useOnBlur";
import { AppPreviewGalleryButtonDisplay } from "./AppPreviewGalleryButtonDisplay/AppPreviewGalleryButtonDisplay";
import { shouldShadowRender } from "@1js/acui-shadow-rendering";
import { MeasuredContext } from "@1js/acui-resize-group";
import { useOnExecute } from "../hooks/useOnExecute";
import { useSelectedIndex } from "../hooks/useSelectedIndex";

const customDirectionalHint = {
  bottomRightEdge: 6,
};
export const useAppPreviewGallery = (
  props: AppPreviewGalleryProps<NeededPropsFromAppGalleryButton>,
  ref: React.Ref<HTMLDivElement>
): AppPreviewGalleryState => {
  const {
    id,
    automationType,
    uniqueId,
    location,
    controlInMenuRendererFunction,
    deferredMenuDefinition,
    downChevronIcon,
    downChevronIconSize,
    disabled,
    shadowRenderMenu,
    preventMenuExecution,
    persistMenu,
    expandedOptions,
    galleryButtons,
    selectedId,
    onMenuButtonExecute,
    onAfterMenuDismiss,
    keytipProps,
  } = props;
  const appFlyoutAnchorId = id + "-previewExpandButton";

  const [internalState, setInternalState] =
    React.useState<AppPreviewGalleryInternalState>({
      selectedIndex: useSelectedIndex(galleryButtons, selectedId),
      expanded: false,
      focused: false,
      buttonFocused: false,
    });

  const rootProps = {
    ...props,
    id: `${id}-Wrapper`,
  };

  const containerProps = {
    id,
    "data-automation-type": automationType,
    "data-automation-id": dataUniqueId({
      id: uniqueId!,
      location,
    }),
    "aria-label": props.flyoutAnchorProps && props.flyoutAnchorProps.label,
    "data-contextmenu-id": props.contextMenuMap?.primaryMenuId,
  };

  const measuredContext = React.useContext(MeasuredContext);
  const onFocus = useOnFocus(internalState, setInternalState);
  const onBlur = useOnBlur(internalState, setInternalState);
  const onExecute = useOnExecute(
    onMenuButtonExecute,
    internalState,
    setInternalState
  );

  const responsiveRenderingHiddenFlyoutProps = useResponsiveRenderingIsEnabled()
    ? {
        "aria-hidden": false,
        "data-focusable": true,
        deferredMenuDefinition,
        directionalHint: customDirectionalHint.bottomRightEdge,
        menuDefinition: expandedOptions,
        terminalParentControlId: id,
        onExecute,
        onAfterMenuDismiss,
        ...props.flyoutAnchorProps,
      }
    : {};

  // These props may be undefined and need to have a fallback
  const hiddenFlyoutAnchorPropsWithFallback = {
    icon: props.flyoutAnchorProps?.icon ?? undefined,
    label: props.flyoutAnchorProps?.label ?? props.menuLauncherLabel,
  };

  const hiddenFlyoutAnchorProps = {
    id: `${idWithSuffix(id, ghostFlyoutIdSuffix)}`,
    location,
    controlInMenuRendererFunction,
    downChevronIcon,
    downChevronIconSize,
    "aria-hidden": true,
    "data-focusable": false,
    ...responsiveRenderingHiddenFlyoutProps,
    ...hiddenFlyoutAnchorPropsWithFallback,
    keytipProps,
  } as AppPreviewGalleryFlyoutAnchorProps;

  const buttonDisplayProps = {
    ...props,
    onFocus,
    onBlur,
    location,
    selectedId,
    id: `${id}-ButtonDisplay`,
  };

  const root = slot.always(
    getIntrinsicElementProps("div", { ref, ...rootProps }),
    { elementType: "div" }
  );

  const container = slot.always(props.container, {
    defaultProps: containerProps,
    elementType: "div",
  });

  const hiddenFlyoutAnchor = slot.always(props.hiddenFlyoutAnchor, {
    defaultProps: hiddenFlyoutAnchorProps,
    elementType: AppPreviewGalleryFlyoutAnchor,
  });

  const actionFlyoutAnchor = slot.always(props.actionFlyoutAnchor, {
    defaultProps: {
      id: appFlyoutAnchorId,
      onExecute,
      onAfterMenuDismiss,
      menuDefinition: expandedOptions,
      onFocus,
      onBlur,
      location,
      "aria-haspopup": true,
      "aria-expanded": !disabled && internalState.expanded,
      disabled,
      deferredMenuDefinition,
      directionalHint: customDirectionalHint.bottomRightEdge,
      label: props.menuLauncherLabel,
      hideLabel: true,
      dataAutomationType: props.dataAutomationType || "AppPreviewGalleryMenu",
      dataUniqueId: dataUniqueId(
        {
          id: uniqueId!,
          location,
        },
        "previewExpandButton"
      ),
      controlInMenuRendererFunction,
      downChevronIcon,
      downChevronIconSize,
      fancyTootipTopMargin: 3,
      persistMenu,
      shadowRenderMenu:
        location === UILocation.Ribbon &&
        shouldShadowRender(shadowRenderMenu, measuredContext.isMeasured),
      preventMenuExecution,
      terminalParentControlId: id,
      showTooltip: true,
      keytipProps,
      size: props.size,
      ribbonMode: props.ribbonMode,
      isV9MenuEnabled: props.isV9MenuEnabled,
      isV9ContextMenuEnabled: props.isV9ContextMenuEnabled,
      isV9FloatieEnabled: props.isV9FloatieEnabled,
      alignV9MenuToEnd: true,
    },
    elementType: AppPreviewGalleryFlyoutAnchor,
  });

  const buttonDisplay = slot.always(props.buttonDisplay, {
    defaultProps: buttonDisplayProps,
    elementType: AppPreviewGalleryButtonDisplay,
  });

  return {
    ...props,
    // reduce the potential size of the gallery button to medium and large (preview gallery can't be small)
    size:
      props.size && props.size.toLowerCase() === "large" ? "large" : "medium",
    components: {
      root: "div",
      container: "div",
      hiddenFlyoutAnchor: AppPreviewGalleryFlyoutAnchor,
      actionFlyoutAnchor: AppPreviewGalleryFlyoutAnchor,
      buttonDisplay: AppPreviewGalleryButtonDisplay,
    },
    root,
    container,
    hiddenFlyoutAnchor,
    actionFlyoutAnchor,
    buttonDisplay,
  } as AppPreviewGalleryState;
};
